import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Table, Badge, Button, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Download, TrendingUp, TrendingDown, MoveRight } from 'lucide-react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { chartColor, tooltipBox, useChartTheme } from '../components/chartTheme';
import PlanFilter, { planKey, planLabel, planRank, planOptions as listPlans } from '../components/PlanFilter';
import { reportService } from '../services/apiService';
import { useInsightData } from '../hooks/useInsightData';
import { useIsMobile } from '../hooks/useMediaQuery';
import StackedTable from '../components/StackedTable';
import SortableHeader from '../components/SortableHeader';
import ReportHeader from '../components/ReportHeader';
import { LoadingState, ErrorState } from '../components/PageState';
import { EMPTY, formatDate, formatMonthYear, formatNumber, formatTime } from '../utils/format';
import { rowClick } from '../utils/rows';
import { downloadCsv } from '../utils/csv';

/**
 * Is Team Today being used more or less over time? Team Today records no logins or page views, so
 * every figure counts people doing something: changing whereabouts, planning days, booking rooms
 * and visitors. The backend sends the chosen window plus the 52 weeks before it, so every shown
 * week has its year-ago twin 52 rows earlier.
 */

const WINDOWS = [
  { value: 13, label: 'Last 13 weeks' },
  { value: 26, label: 'Last 26 weeks' },
  { value: 52, label: 'Last 52 weeks' },
  { value: 104, label: 'Last 2 years' },
];

const YEAR_WEEKS = 52;
const SMOOTH_WEEKS = 4;
const move = (recent, previous) => (recent == null || previous == null ? null : recent - previous);

/**
 * Companies table columns: the value each sorts by and which way the first click sorts. The change
 * columns sort by the move in people or days a week, not the percentage shown, so a company going
 * from 1 planner to 3 does not outrank one that lost 40.
 */
const COMPANY_SORTS = {
  name: { value: (c) => (c.name || c.companyId).toLowerCase(), first: 'asc' },
  plan: { value: (c) => planRank(planKey(c)), first: 'asc' },
  users: { value: (c) => c.users, first: 'desc' },
  paidUsers: { value: (c) => c.paidUsers ?? 0, first: 'desc' },
  recentPlanners: { value: (c) => c.recentPlanners, first: 'desc' },
  previousPlanners: { value: (c) => c.previousPlanners, first: 'desc' },
  plannerMove: { value: (c) => move(c.recentPlanners, c.previousPlanners), first: 'asc' },
  recentPersonDays: { value: (c) => c.recentPersonDays, first: 'desc' },
  previousPersonDays: { value: (c) => c.previousPersonDays, first: 'desc' },
  personDayMove: { value: (c) => move(c.recentPersonDays, c.previousPersonDays), first: 'asc' },
};
const MOVE_TITLE = 'Sorts by the change in people or days a week, not the percentage';

/** Sorts on missing values last whichever way round, then by company name. */
const compareCompanies = ({ field, direction }) => (a, b) => {
  const { value } = COMPANY_SORTS[field];
  const va = value(a);
  const vb = value(b);
  if (va == null || vb == null) return va == null && vb == null ? 0 : va == null ? 1 : -1;
  const order = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
  return (direction === 'asc' ? order : -order)
    || (a.name || a.companyId).localeCompare(b.name || b.companyId);
};


/**
 * Sort orders for the phone layout, where the stacked table has no headers to click, and the words
 * for the note under the table header. Keys are "field:direction".
 */
const SORT_CHOICES = [
  { key: 'plannerMove:asc', label: 'Biggest drop in active planners' },
  { key: 'plannerMove:desc', label: 'Biggest rise in active planners' },
  { key: 'recentPlanners:desc', label: 'Most active planners' },
  { key: 'personDayMove:asc', label: 'Biggest drop in person-days' },
  { key: 'personDayMove:desc', label: 'Biggest rise in person-days' },
  { key: 'recentPersonDays:desc', label: 'Most person-days' },
  { key: 'users:desc', label: 'Most users' },
  { key: 'paidUsers:desc', label: 'Most paid seats' },
  { key: 'name:asc', label: 'Name, A to Z' },
  { key: 'plan:asc', label: 'Plan' },
];
const SORT_FIELD_WORDS = {
  name: 'name',
  plan: 'plan',
  users: 'users',
  paidUsers: 'paid seats',
  recentPlanners: 'active planners',
  previousPlanners: 'active planners before',
  plannerMove: 'change in active planners',
  recentPersonDays: 'person-days',
  previousPersonDays: 'person-days before',
  personDayMove: 'change in person-days',
};
const sortKey = (sort) => `${sort.field}:${sort.direction}`;
/** "Sorted by biggest drop in active planners", or the column and direction for other orders. */
const sortNote = (sort) => {
  const choice = SORT_CHOICES.find((c) => c.key === sortKey(sort));
  if (choice) return `Sorted by ${choice.label.charAt(0).toLowerCase()}${choice.label.slice(1)}`;
  return `Sorted by ${SORT_FIELD_WORDS[sort.field]}, ${sort.direction === 'asc' ? 'lowest' : 'highest'} first`;
};
/** The weekly table's date column stays in view while its 15 figures scroll sideways on a phone. */
const STICKY_FIRST_COLUMN = { position: 'sticky', left: 0, zIndex: 1 };

/** How often to ask again while the server is still reading the figures. */
const POLL_MS = 5000;
const COMPANY_ROWS = 100;

const count = (v) => {
  if (v == null) return EMPTY;
  return Math.abs(v) < 10
    ? v.toLocaleString('en-GB', { maximumFractionDigits: 1 })
    : Math.round(v).toLocaleString('en-GB');
};
const share = (v) => (v == null ? EMPTY : `${(v * 100).toFixed(1)}%`);
const compactAxis = new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 });
const signedPct = (change) => {
  const rounded = Math.round(change * 100) || 0;
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
};

const METRICS = {
  activePlanners: { label: 'Weekly active planners', format: count, activity: true },
  personDays: { label: 'Planned person-days', format: count },
  activeCompanies: { label: 'Active companies', format: count, activity: true },
  planningShare: { label: 'Share of users planning', format: share, isShare: true },
  officeDays: { label: 'Office days', format: count },
  deskDays: { label: 'Desk bookings', format: count },
  parkingDays: { label: 'Parking bookings', format: count },
  leaveDays: { label: 'Leave days', format: count },
  meetingRoomBookings: { label: 'Meeting room bookings', format: count },
  visitorBookings: { label: 'Visitor bookings', format: count },
  newUsers: { label: 'New users', format: count },
  newCompanies: { label: 'New companies', format: count },
  edits: { label: 'Whereabouts changes', format: count, activity: true },
  plannedUsers: { label: 'People with a plan', format: count },
  registeredUsers: { label: 'Registered users', format: count },
};

/** Small multiples under the headline charts. Up is not always good here (leave), so their deltas stay neutral. */
const FEATURES = [
  { key: 'officeDays', note: 'Days planned in the office, by the week planned' },
  { key: 'deskDays', note: 'Days with a desk booked, by the week planned' },
  { key: 'parkingDays', note: 'Days with parking booked, by the week planned' },
  { key: 'leaveDays', note: 'Leave booked, approved or pending, by the week taken' },
  { key: 'meetingRoomBookings', note: 'Bookings made in the week' },
  { key: 'visitorBookings', note: 'Bookings made in the week' },
  { key: 'newUsers', note: 'Created in the week and still on the system' },
  { key: 'newCompanies', note: 'Companies created in the week' },
];

const TABLE_COLUMNS = [
  'activePlanners', 'activeCompanies', 'edits', 'plannedUsers', 'personDays', 'planningShare', 'officeDays',
  'deskDays', 'parkingDays', 'leaveDays', 'meetingRoomBookings', 'visitorBookings', 'newUsers', 'newCompanies',
  'registeredUsers',
];

const parseDay = (iso) => new Date(`${iso}T00:00:00`);
const formatDay = (iso) => (iso ? formatDate(parseDay(iso)) : EMPTY);
const monthTick = (iso) => formatMonthYear(parseDay(iso));
/** The first week starting in each month, so a month is labelled once however many weeks it has. */
const monthTicks = (rows) => rows
  .filter((r, i) => i > 0 && r.weekStart.slice(0, 7) !== rows[i - 1].weekStart.slice(0, 7))
  .map((r) => r.weekStart);
const addWeeks = (iso, weeks) => {
  const d = parseDay(iso);
  d.setDate(d.getDate() + weeks * 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const formatStamp = (value) => (value
  ? new Date(value).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  : '');

/** Direction word, icon and ink for a trend. Status ink only where up is good. */
const DIRECTIONS = {
  UP: { word: 'Up', Icon: TrendingUp, ink: 'var(--color-success-fg)' },
  DOWN: { word: 'Down', Icon: TrendingDown, ink: 'var(--color-danger-fg)' },
  FLAT: { word: 'Flat', Icon: MoveRight, ink: 'var(--fg-muted)' },
};
const directionOf = (change, threshold) => {
  if (change == null) return null;
  if (change > threshold) return 'UP';
  if (change < -threshold) return 'DOWN';
  return 'FLAT';
};

/** "Up 8%" or, for a share, "Up 2.1 points": a relative change of a percentage reads as a typo. */
const changeText = (summary, base, isShare) => {
  const change = base === 'YEAR' ? summary.changeVsYearAgo : summary.changeVsPrevious;
  const baseValue = base === 'YEAR' ? summary.yearAgo : summary.previous;
  if (change == null) return null;
  if (isShare) {
    const points = (summary.recent - baseValue) * 100;
    return `${points >= 0 ? '+' : ''}${points.toFixed(1)} points`;
  }
  return signedPct(change);
};
const BASIS_WORDS = { YEAR: 'on the same weeks last year', PREVIOUS: 'on the 13 weeks before' };

const TrendLine = ({ summary, isShare, neutral = false }) => {
  if (!summary?.trend) return <small className="text-muted">Not enough history to compare</small>;
  const { word, Icon, ink } = DIRECTIONS[summary.trend];
  const color = neutral ? 'var(--fg-default)' : ink;
  const text = changeText(summary, summary.basis, isShare);
  return (
    <span style={{ fontSize: 13 }}>
      <Icon size={16} color={color} aria-hidden="true" className="me-1" style={{ verticalAlign: '-3px' }} />
      <strong style={{ color }}>{word}</strong>{' '}
      <span className="text-muted">
        {summary.trend === 'FLAT' ? `(${text}) ` : `${text.replace(/^\+|^-/, '')} `}
        {BASIS_WORDS[summary.basis]}
      </span>
    </span>
  );
};

/**
 * One headline figure for a chart's header: average per week over the latest 13 weeks, and how it
 * moved. It sits with its chart rather than in a tile row above, so each number is said once.
 */
const HeadlineFigure = ({ metricKey, summary, meta, primary }) => {
  const metric = METRICS[metricKey];
  const other = summary?.basis === 'YEAR' ? 'PREVIOUS' : 'YEAR';
  const otherText = summary ? changeText(summary, other, metric.isShare) : null;
  let footnote = null;
  if (otherText) {
    footnote = `${otherText} ${BASIS_WORDS[other]}`;
  } else if (summary?.basis === 'PREVIOUS' && metric.activity && meta.activityHistoryStart) {
    footnote = `Year-on-year from ${formatDay(addWeeks(meta.activityHistoryStart, YEAR_WEEKS + meta.comparisonWeeks))}`;
  }
  // The card header is bold; only the figure itself should be
  return (
    <div className="text-md-end" style={{ fontWeight: 400 }}>
      <div className="d-flex align-items-baseline justify-content-md-end gap-2">
        <span className={primary ? 'text-primary' : undefined} style={{ fontSize: 24, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {metric.format(summary?.recent)}
        </span>
        <span className="text-muted small">{metric.isShare ? 'average week' : 'a week'}, last {meta.comparisonWeeks} weeks</span>
      </div>
      <TrendLine summary={summary} isShare={metric.isShare} />
      {footnote && <small className="text-muted d-block">{footnote}</small>}
    </div>
  );
};

const LineKey = ({ color }) => (
  <span aria-hidden="true" style={{ display: 'inline-block', width: 14, height: 2, borderRadius: 1, background: color }} />
);

const TrendTooltip = ({ active, payload, metricKey, compare, smoothed }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const { format } = METRICS[metricKey];
  const current = row[metricKey];
  const prior = row[`${metricKey}YearAgo`];
  return (
    <div style={tooltipBox}>
      <div className="text-muted small mb-1">
        Week of {formatDay(row.weekStart)}{smoothed ? `, ${SMOOTH_WEEKS}-week average` : ''}
      </div>
      <div className="d-flex align-items-center gap-2">
        {compare && <LineKey color={chartColor('--chart-emphasis')} />}
        <strong>{format(current)}</strong>
        {compare && <span className="text-muted">this year</span>}
      </div>
      {compare && prior != null && (
        <div className="d-flex align-items-center gap-2">
          <LineKey color={chartColor('--chart-context')} />
          <strong>{format(prior)}</strong>
          <span className="text-muted">a year earlier</span>
        </div>
      )}
    </div>
  );
};

/**
 * One metric over the window. With a year-ago line it is two lines (emphasis and grey context)
 * with a legend; on its own it is a single line over a light wash and the card title names it.
 * Full-size charts mark and label the latest value, so the lines differ by more than colour.
 */
const TrendChart = ({ rows, metricKey, height, compare = true, compact = false, smoothed }) => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const metric = METRICS[metricKey];
  const priorKey = `${metricKey}YearAgo`;
  const hasData = rows.some((r) => r[metricKey] != null);
  const showPrior = compare && rows.some((r) => r[priorKey] != null);
  if (!hasData) {
    return <div className="text-center text-muted p-4" style={{ minHeight: height }}>No figures in this window</div>;
  }
  const accent = t.colors.emphasis;
  const neutral = t.colors.context;
  const surface = chartColor('--bg-default');
  const endIndex = compact ? -1 : rows.reduce((last, r, i) => (r[metricKey] != null ? i : last), -1);
  const endDot = ({ index, cx, cy }) => (index === endIndex
    ? <circle key="end-dot" cx={cx} cy={cy} r={4} fill={accent} stroke={surface} strokeWidth={2} />
    : <g key={`dot-${index}`} />);
  const endLabel = ({ index, x, y, value }) => (index === endIndex ? (
    <text x={x} y={y - 12} textAnchor="end" fill={chartColor('--fg-default')} fontSize={12} fontWeight={600}>
      {metric.format(value)}
    </text>
  ) : null);
  const { muted } = t.colors;
  const axisFormat = metric.isShare ? (v) => `${Math.round(v * 100)}%` : (v) => compactAxis.format(v);
  return (
    <>
      {showPrior && (
        <div className="d-flex flex-wrap gap-3 mb-2" style={{ fontSize: 13 }}>
          <span className="d-inline-flex align-items-center gap-2"><LineKey color={accent} />This year</span>
          <span className="d-inline-flex align-items-center gap-2"><LineKey color={neutral} />Same week last year</span>
        </div>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={rows} margin={{ top: 8, right: compact || isMobile ? 8 : 16, left: 0, bottom: 0 }}>
          <CartesianGrid {...t.grid} />
          <XAxis
            dataKey="weekStart"
            {...t.xAxis}
            ticks={monthTicks(rows)}
            tickFormatter={monthTick}
            interval="equidistantPreserveStart"
            minTickGap={compact ? 40 : 24}
          />
          <YAxis
            {...t.yAxis}
            domain={[0, 'auto']}
            tickFormatter={axisFormat}
            width={compact || isMobile ? 40 : 48}
            tickCount={compact ? 3 : 5}
            allowDecimals={metric.isShare}
          />
          <Tooltip
            {...t.tooltip}
            content={<TrendTooltip metricKey={metricKey} compare={showPrior} smoothed={smoothed} />}
            cursor={{ stroke: muted, strokeWidth: 1 }}
          />
          {showPrior && (
            <Line
              type="monotone"
              dataKey={priorKey}
              stroke={neutral}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: neutral, stroke: surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          )}
          {showPrior ? (
            <Line
              type="monotone"
              dataKey={metricKey}
              stroke={accent}
              strokeWidth={2}
              dot={endDot}
              label={endLabel}
              activeDot={{ r: 5, fill: accent, stroke: surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          ) : (
            <Area
              type="monotone"
              dataKey={metricKey}
              stroke={accent}
              strokeWidth={2}
              fill={accent}
              fillOpacity={0.1}
              dot={endDot}
              label={endLabel}
              activeDot={{ r: 5, fill: accent, stroke: surface, strokeWidth: 2 }}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </>
  );
};

/** A chart card; `figure` (a HeadlineFigure) sits on the right of the header, or under the title when it runs out of room. */
const ChartCard = ({ title, note, figure, children }) => (
  <Card className="shadow-sm h-100">
    <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-start gap-2 gap-md-3">
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        <h5 className="mb-0">{title}</h5>
        {note && <small className="text-muted">{note}</small>}
      </div>
      {figure && <div className="flex-shrink-0">{figure}</div>}
    </Card.Header>
    <Card.Body>{children}</Card.Body>
  </Card>
);

/** A company's change between the two periods: arrow, signed percentage, and the move in people. */
const CompanyChange = ({ recent, previous, change, threshold }) => {
  if (previous == null) return <span className="text-muted">{EMPTY}</span>;
  if (change == null) return recent > 0 ? <Badge bg="secondary">New</Badge> : <span className="text-muted">{EMPTY}</span>;
  const direction = directionOf(change, threshold);
  const { Icon, ink } = DIRECTIONS[direction];
  return (
    <span className="d-inline-flex align-items-center gap-1 justify-content-end">
      <Icon size={14} color={ink} aria-hidden="true" />
      {signedPct(change)}
    </span>
  );
};

/**
 * The headline sentence. Year-on-year changes cancel out the season, so when any headline metric
 * has one, the call rests on those; a metric that can only compare with the 13 weeks before says so.
 */
const Verdict = ({ planners, days }) => {
  const measured = [planners, days].filter((s) => s?.trend);
  if (!measured.length) return null;
  const yearly = measured.filter((s) => s.basis === 'YEAR');
  const deciding = yearly.length ? yearly : measured;
  const trends = new Set(deciding.map((s) => s.trend));
  const trend = trends.size === 1 ? deciding[0].trend : null;
  const heading = trend ? `Engagement is ${DIRECTIONS[trend].word.toLowerCase()}` : 'Mixed signals';
  const { Icon } = DIRECTIONS[trend || 'FLAT'];
  const describe = (s, label) => {
    if (!s?.trend) return null;
    const text = changeText(s, s.basis, false);
    const sentence = s.trend === 'FLAT'
      ? `${label} are flat ${BASIS_WORDS[s.basis]} (${text}).`
      : `${label} are ${DIRECTIONS[s.trend].word.toLowerCase()} ${text.replace(/^\+|^-/, '')} ${BASIS_WORDS[s.basis]}.`;
    const caveat = yearly.length && s.basis === 'PREVIOUS'
      ? ' There is no year-ago figure yet, so seasonal dips can skew that one.'
      : '';
    return sentence + caveat;
  };
  return (
    <Card className="shadow-sm mb-4">
      <Card.Body className="d-flex gap-3 align-items-start">
        <Icon size={28} className="flex-shrink-0 mt-1" aria-hidden="true" />
        <div>
          <h4 className="mb-1">{heading}</h4>
          <div className="text-muted">
            {[describe(days, 'Planned person-days'), describe(planners, 'Weekly active planners')].filter(Boolean).join(' ')}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const BIGGEST_DROPS = 5;

/**
 * The companies whose active planners fell the most, by the move in people rather than the
 * percentage, so the outreach list sits under the verdict instead of below every chart.
 */
const BiggestDrops = ({ companies, meta, onShowAll }) => {
  const navigate = useNavigate();
  const drops = companies
    .filter((c) => directionOf(c.plannerChange, meta.flatThreshold) === 'DOWN')
    .sort(compareCompanies({ field: 'plannerMove', direction: 'asc' }))
    .slice(0, BIGGEST_DROPS);
  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h5 className="mb-0">Biggest drops</h5>
          <small className="text-muted">
            Companies that lost the most active planners, last {meta.comparisonWeeks} weeks against the {meta.comparisonWeeks} before.
          </small>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={onShowAll}>See all companies</Button>
      </Card.Header>
      <Card.Body className="p-0">
        {drops.length === 0 ? (
          <div className="p-4 text-center text-muted">
            No company's active planners fell by more than {(meta.flatThreshold * 100).toFixed(0)}%.
          </div>
        ) : (
          <StackedTable responsive hover className="mb-0 align-middle" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <thead className="table-light">
              <tr>
                <th>Company</th>
                <th>Plan</th>
                <th className="text-end">Active planners</th>
                <th className="text-end">Before</th>
                <th className="text-end">Change</th>
              </tr>
            </thead>
            <tbody>
              {drops.map((c) => (
                <tr key={c.companyId} onClick={rowClick(() => navigate(`/companies/${c.companyId}`))} style={{ cursor: 'pointer' }}>
                  <td><Link className="cell-link" to={`/companies/${c.companyId}`}>{c.name || c.companyId}</Link></td>
                  <td>{planLabel(planKey(c))}</td>
                  <td className="text-end"><strong>{count(c.recentPlanners)}</strong></td>
                  <td className="text-end text-muted">{count(c.previousPlanners)}</td>
                  <td className="text-end">
                    <CompanyChange recent={c.recentPlanners} previous={c.previousPlanners} change={c.plannerChange} threshold={meta.flatThreshold} />
                  </td>
                </tr>
              ))}
            </tbody>
          </StackedTable>
        )}
      </Card.Body>
    </Card>
  );
};

/**
 * Shown until the server has read the figures. It reads years of whereabouts and the whole audit
 * log in the background, then cuts every window from that, so only the first load waits.
 */
const BuildingNotice = ({ building }) => (
  <Card className="shadow-sm">
    <Card.Body className="d-flex gap-3 align-items-start p-4">
      {building.startedAt && (
        <Spinner animation="border" size="sm" className="flex-shrink-0 mt-1" role="status">
          <span className="visually-hidden">Reading the figures</span>
        </Spinner>
      )}
      <div>
        <h5 className="mb-1">{building.startedAt ? 'Reading the figures from the database' : 'The figures could not be read'}</h5>
        <p className="text-muted mb-0">
          {building.startedAt
            ? `Started at ${formatTime(building.startedAt)}. This reads years of whereabouts and the whole audit log, so the first load after a deploy can take a few minutes. The page updates by itself, and every window is instant once it is done.`
            : `It will try again at ${formatTime(building.retryAt)}. Leave this page open and it will pick the figures up when they are ready.`}
        </p>
        {building.lastError && <Alert variant="warning" className="mt-3 mb-0 small">Last attempt failed: {building.lastError}</Alert>}
      </div>
    </Card.Body>
  </Card>
);

const EngagementReport = () => {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState(52);
  const [smoothed, setSmoothed] = useState(true);
  const [companySort, setCompanySort] = useState({ field: 'plannerMove', direction: 'asc' });
  const [paying, setPaying] = useState('all');
  const [hiddenPlans, setHiddenPlans] = useState(() => new Set());
  const [search, setSearch] = useState('');
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const [showWeeklyTable, setShowWeeklyTable] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const isMobile = useIsMobile();
  const { data, loading, error, reload } = useInsightData(() => reportService.engagement({ weeks }), [weeks, pollTick]);

  // A 202 carries the build's progress instead of the report
  const building = data?.status === 'BUILDING' ? data : null;
  const report = building ? null : data;

  useEffect(() => {
    if (!building || loading) return undefined;
    const timer = setTimeout(() => setPollTick((t) => t + 1), POLL_MS);
    return () => clearTimeout(timer);
  }, [building, loading]);

  const summaries = useMemo(() => Object.fromEntries((report?.summary || []).map((s) => [s.key, s])), [report]);

  /** Window weeks for the charts, each with its (optionally smoothed) value and the value 52 weeks earlier. */
  const rows = useMemo(() => {
    if (!report) return [];
    const all = report.weeks;
    const valueAt = (key, i) => {
      if (i < 0) return null;
      if (!smoothed) return all[i][key] ?? null;
      if (i < SMOOTH_WEEKS - 1) return null;
      let sum = 0;
      for (let j = i - SMOOTH_WEEKS + 1; j <= i; j++) {
        const v = all[j][key];
        if (v == null) return null;
        sum += v;
      }
      return sum / SMOOTH_WEEKS;
    };
    const keys = Object.keys(METRICS);
    return all.flatMap((week, i) => {
      if (week.weekStart < report.meta.windowStart) return [];
      const row = { weekStart: week.weekStart };
      keys.forEach((k) => {
        row[k] = valueAt(k, i);
        row[`${k}YearAgo`] = valueAt(k, i - YEAR_WEEKS);
      });
      return [row];
    });
  }, [report, smoothed]);

  const windowWeeks = useMemo(
    () => (report ? report.weeks.filter((w) => w.weekStart >= report.meta.windowStart) : []),
    [report],
  );

  const planOptions = useMemo(() => listPlans(report?.companies || []), [report]);

  const sortCompanies = (field) => setCompanySort((prev) => (prev.field === field
    ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
    : { field, direction: COMPANY_SORTS[field].first }));

  // Paying means paid seats, as on the Reports page
  const companies = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (report?.companies || []).filter((c) => {
      if (q && !(c.name || c.companyId).toLowerCase().includes(q)) return false;
      if (paying === 'paying' && !((c.paidUsers ?? 0) > 0)) return false;
      if (paying === 'free' && (c.paidUsers ?? 0) > 0) return false;
      return !hiddenPlans.has(planKey(c));
    });
    return [...list].sort(compareCompanies(companySort));
  }, [report, companySort, search, paying, hiddenPlans]);

  const snakeCase = (key) => key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

  const exportWeeks = () => downloadCsv(
    'engagement-weekly',
    ['week_start', ...TABLE_COLUMNS.map(snakeCase)],
    windowWeeks.map((w) => [w.weekStart, ...TABLE_COLUMNS.map((k) => w[k])]),
  );

  // The companies the table shows, in its order, all of them rather than the first page
  const exportCompanies = () => downloadCsv(
    'engagement-companies',
    ['company_id', 'company_name', 'plan', 'enabled', 'users', 'paid_seats', 'active_planners', 'active_planners_before',
      'planner_change', 'person_days', 'person_days_before', 'person_day_change'],
    companies.map((c) => [
      c.companyId, c.name, c.accountType || '', c.enabled ? 'yes' : 'no', c.users, c.paidUsers ?? 0,
      c.recentPlanners, c.previousPlanners, c.plannerChange, c.recentPersonDays, c.previousPersonDays, c.personDayChange,
    ]),
  );

  // From the biggest drops card: the full table in the same order, scrolled into view
  const showBiggestDrops = () => {
    setCompanySort({ field: 'plannerMove', direction: 'asc' });
    document.getElementById('engagement-companies')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const meta = report?.meta;
  const visibleCompanies = showAllCompanies ? companies : companies.slice(0, COMPANY_ROWS);
  const sortProps = { sortField: companySort.field, sortDirection: companySort.direction, onSort: sortCompanies };
  const smoothNote = smoothed ? ` Each point is the average of that week and the ${SMOOTH_WEEKS - 1} before.` : '';

  return (
    <Container fluid className="py-4">
      <ReportHeader
        title="Engagement"
        description="Is Team Today being used more or less over time? Weekly activity across every company."
      />

      <Card className="shadow-sm mb-4">
        <Card.Body className="d-flex flex-wrap gap-3 align-items-end filter-bar">
          {/* On phones each select takes a full line, so its option text is not cut off */}
          <Form.Group controlId="engagementWindow" className={isMobile ? 'filter-bar-wide' : undefined}>
            <Form.Label className="small text-muted mb-1">Show</Form.Label>
            <Form.Select size="sm" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} className="pe-5" style={{ width: isMobile ? '100%' : 'auto' }}>
              {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="engagementSmoothing" className={isMobile ? 'filter-bar-wide' : undefined}>
            <Form.Label className="small text-muted mb-1">Smoothing</Form.Label>
            <Form.Select size="sm" value={smoothed ? 'smooth' : 'weekly'} onChange={(e) => setSmoothed(e.target.value === 'smooth')} className="pe-5" style={{ width: isMobile ? '100%' : 'auto' }}>
              <option value="smooth">{SMOOTH_WEEKS}-week average</option>
              <option value="weekly">None, each week</option>
            </Form.Select>
          </Form.Group>
          {meta && (
            <div className="ms-auto small text-muted text-md-end filter-bar-wide">
              Figures read at {formatStamp(meta.generatedAt)} and rebuilt daily. Weeks run Monday to Sunday, up to {formatDay(meta.lastWeek)}.
              {meta.excludedCompanies > 0 && ` ${meta.excludedCompanies} internal or test ${meta.excludedCompanies === 1 ? 'company' : 'companies'} left out.`}
            </div>
          )}
        </Card.Body>
      </Card>

      {error ? (
        <Card className="shadow-sm"><ErrorState inCard message={error} onRetry={reload} /></Card>
      ) : !data ? (
        <Card className="shadow-sm"><LoadingState /></Card>
      ) : building ? (
        <BuildingNotice building={building} />
      ) : report && (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 120ms ease-out' }} aria-busy={loading}>
          <Verdict planners={summaries.activePlanners} days={summaries.personDays} />

          <BiggestDrops companies={report.companies} meta={meta} onShowAll={showBiggestDrops} />

          <div className="mb-4">
            <ChartCard
              title="Weekly active planners"
              figure={<HeadlineFigure metricKey="activePlanners" summary={summaries.activePlanners} meta={meta} primary />}
              note={`People who changed any whereabouts that week, their own or someone else's.${meta.activityHistoryStart ? ` Recorded from ${formatDay(meta.activityHistoryStart)}.` : ''}${smoothNote}`}
            >
              <TrendChart rows={rows} metricKey="activePlanners" height={isMobile ? 240 : 300} smoothed={smoothed} />
            </ChartCard>
          </div>

          <div className="mb-4">
            <ChartCard
              title="Planned person-days"
              figure={<HeadlineFigure metricKey="personDays" summary={summaries.personDays} meta={meta} />}
              note={`Days people have planned, by the week being planned. Half days count a half; company holidays are left out.${smoothNote}`}
            >
              <TrendChart rows={rows} metricKey="personDays" height={isMobile ? 240 : 300} smoothed={smoothed} />
            </ChartCard>
          </div>

          <Row className="g-4 mb-4">
            <Col lg={6}>
              <ChartCard
                title="Active companies"
                figure={<HeadlineFigure metricKey="activeCompanies" summary={summaries.activeCompanies} meta={meta} />}
                note={`Companies with ${meta.activeCompanyMinPlanners} or more active planners in the week.`}
              >
                <TrendChart rows={rows} metricKey="activeCompanies" height={isMobile ? 200 : 240} smoothed={smoothed} />
              </ChartCard>
            </Col>
            <Col lg={6}>
              <ChartCard
                title="Share of users planning"
                figure={<HeadlineFigure metricKey="planningShare" summary={summaries.planningShare} meta={meta} />}
                note="People with at least one day planned, out of everyone registered at the end of that week."
              >
                <TrendChart rows={rows} metricKey="planningShare" height={isMobile ? 200 : 240} smoothed={smoothed} />
              </ChartCard>
            </Col>
          </Row>

          <h4 className="mb-1">Feature use</h4>
          <p className="text-muted small mb-3">
            Average a week over the last {meta.comparisonWeeks} weeks, and how it moved.
          </p>
          <Row className="g-4 mb-4">
            {FEATURES.map(({ key, note }) => {
              const summary = summaries[key];
              return (
                <Col key={key} xl={3} md={6}>
                  <Card className="shadow-sm h-100">
                    <Card.Body>
                      <h6 className="mb-0">{METRICS[key].label}</h6>
                      <small className="text-muted d-block mb-2">{note}</small>
                      <div className="d-flex align-items-baseline gap-2 mb-1">
                        <span style={{ fontSize: 24, fontWeight: 600 }}>{count(summary?.recent)}</span>
                        <span className="text-muted small">a week</span>
                      </div>
                      <div className="mb-2"><TrendLine summary={summary} neutral /></div>
                      <TrendChart rows={rows} metricKey={key} height={isMobile ? 90 : 130} compare={false} compact smoothed={smoothed} />
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <Card className="shadow-sm mb-4" id="engagement-companies" style={{ scrollMarginTop: 16 }}>
            <Card.Header className="bg-light">
              <Row className="g-2 align-items-center">
                <Col md="auto"><h5 className="mb-0 me-2">Companies</h5></Col>
                <Col md={3}>
                  <Form.Control size="sm" type="search" placeholder="Search company" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search company" />
                </Col>
                <Col xs={6} md="auto">
                  <Form.Select size="sm" value={paying} onChange={(e) => setPaying(e.target.value)} className="pe-5" aria-label="Filter by paying">
                    <option value="all">Paying and free</option>
                    <option value="paying">Paying only</option>
                    <option value="free">Free only</option>
                  </Form.Select>
                </Col>
                <Col xs={6} md="auto">
                  <PlanFilter id="engagementPlans" options={planOptions} hidden={hiddenPlans} onChange={setHiddenPlans} />
                </Col>
                {/* Phones stack each company into a card with no column headers, so the order is a select there */}
                {isMobile && (
                  <Col xs={12}>
                    <Form.Group controlId="engagementSort" className="d-flex align-items-center gap-2">
                      <Form.Label className="small text-muted mb-0 text-nowrap">Sort by</Form.Label>
                      <Form.Select
                        size="sm"
                        value={SORT_CHOICES.some((c) => c.key === sortKey(companySort)) ? sortKey(companySort) : ''}
                        onChange={(e) => {
                          const [field, direction] = e.target.value.split(':');
                          setCompanySort({ field, direction });
                        }}
                      >
                        {!SORT_CHOICES.some((c) => c.key === sortKey(companySort)) && (
                          <option value="" disabled>{sortNote(companySort)}</option>
                        )}
                        {SORT_CHOICES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}
                <Col className="d-flex align-items-center justify-content-md-end flex-wrap gap-2 text-muted small">
                  <span>
                    {formatNumber(companies.length)} of {formatNumber(report.companies.length)} companies.
                    {' '}Last {meta.comparisonWeeks} weeks against the {meta.comparisonWeeks} before, averages a week.
                    {!isMobile && ` ${sortNote(companySort)}.`}
                  </span>
                  <Button variant="outline-secondary" size="sm" onClick={exportCompanies} disabled={!companies.length}>
                    <Download size={14} className="me-1" aria-hidden="true" />Export CSV
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {companies.length === 0 ? (
                <div className="p-4 text-center text-muted">No companies match</div>
              ) : (
                /* On phones each company is a card of its planners, their change and person-days. The
                   paid seats and "Before" columns are desktop only (the change column already compares the
                   periods), so the table fits a tablet */
                <StackedTable responsive hover className="mb-0 align-middle" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <thead className="table-light">
                    <tr>
                      <SortableHeader field="name" {...sortProps}>Company</SortableHeader>
                      <SortableHeader field="plan" {...sortProps}>Plan</SortableHeader>
                      <SortableHeader field="users" {...sortProps} align="text-end d-none d-md-table-cell">Users</SortableHeader>
                      <SortableHeader field="paidUsers" {...sortProps} align="text-end d-none d-xl-table-cell">Paid seats</SortableHeader>
                      <SortableHeader field="recentPlanners" {...sortProps} align="text-end">Active planners</SortableHeader>
                      <SortableHeader field="previousPlanners" {...sortProps} align="text-end d-none d-xl-table-cell">Before</SortableHeader>
                      <SortableHeader field="plannerMove" {...sortProps} align="text-end" title={MOVE_TITLE}>Change</SortableHeader>
                      <SortableHeader field="recentPersonDays" {...sortProps} align="text-end">Person-days</SortableHeader>
                      <SortableHeader field="previousPersonDays" {...sortProps} align="text-end d-none d-xl-table-cell">Before</SortableHeader>
                      <SortableHeader field="personDayMove" {...sortProps} align="text-end d-none d-md-table-cell" title={MOVE_TITLE}>Change</SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCompanies.map((c) => (
                      <tr key={c.companyId} onClick={rowClick(() => navigate(`/companies/${c.companyId}`))} style={{ cursor: 'pointer' }}>
                        <td>
                          <Link className="cell-link" to={`/companies/${c.companyId}`}>{c.name || c.companyId}</Link>
                          {c.name && !c.enabled && <Badge bg="secondary" className="ms-2">Disabled</Badge>}
                        </td>
                        <td>{planLabel(planKey(c))}</td>
                        <td className="text-end d-none d-md-table-cell">{formatNumber(c.users)}</td>
                        <td className="text-end d-none d-xl-table-cell">{c.paidUsers ? formatNumber(c.paidUsers) : <span className="text-muted">{EMPTY}</span>}</td>
                        <td className="text-end"><strong>{count(c.recentPlanners)}</strong></td>
                        <td className="text-end text-muted d-none d-xl-table-cell">{count(c.previousPlanners)}</td>
                        <td className="text-end">
                          <CompanyChange recent={c.recentPlanners} previous={c.previousPlanners} change={c.plannerChange} threshold={meta.flatThreshold} />
                        </td>
                        <td className="text-end">{count(c.recentPersonDays)}</td>
                        <td className="text-end text-muted d-none d-xl-table-cell">{count(c.previousPersonDays)}</td>
                        <td className="text-end d-none d-md-table-cell">
                          <CompanyChange recent={c.recentPersonDays} previous={c.previousPersonDays} change={c.personDayChange} threshold={meta.flatThreshold} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </StackedTable>
              )}
            </Card.Body>
            {companies.length > COMPANY_ROWS && (
              <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <small className="text-muted">
                  Showing {formatNumber(visibleCompanies.length)} of {formatNumber(companies.length)}
                </small>
                <Button variant="link" size="sm" onClick={() => setShowAllCompanies(!showAllCompanies)}>
                  {showAllCompanies ? `Show first ${COMPANY_ROWS}` : `Show all ${formatNumber(companies.length)}`}
                </Button>
              </Card.Footer>
            )}
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-0">Weekly figures</h5>
                <small className="text-muted">Every number behind the charts, one row per week, without the averaging.</small>
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-end">
                <Button variant="outline-secondary" size="sm" onClick={exportWeeks} disabled={!windowWeeks.length}>
                  <Download size={14} className="me-1" aria-hidden="true" />Export weekly figures
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={() => setShowWeeklyTable(!showWeeklyTable)} aria-expanded={showWeeklyTable}>
                  {showWeeklyTable ? 'Hide' : 'Show'}
                </Button>
              </div>
            </Card.Header>
            {showWeeklyTable && (
              <Card.Body className="p-0">
                <Table responsive hover size="sm" className="mb-0 align-middle" style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                  <thead className="table-light">
                    <tr>
                      <th style={isMobile ? STICKY_FIRST_COLUMN : undefined}>Week of</th>
                      {TABLE_COLUMNS.map((k) => <th key={k} className="text-end">{METRICS[k].label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {[...windowWeeks].reverse().map((w) => (
                      <tr key={w.weekStart}>
                        <td style={isMobile ? STICKY_FIRST_COLUMN : undefined}>{formatDay(w.weekStart)}</td>
                        {TABLE_COLUMNS.map((k) => <td key={k} className="text-end">{METRICS[k].format(w[k])}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            )}
          </Card>

          <Card className="shadow-sm">
            <Card.Body className="small text-muted">
              <p className="mb-2">
                <strong style={{ color: 'var(--fg-default)' }}>How this is worked out.</strong> Team Today records no logins or page views,
                so these figures count people doing something. Someone who only looks at the wallchart does not show up.
              </p>
              <p className="mb-2">
                Active planners come from the audit log, which records every whereabouts change and who made it. Automatic office check-ins are left out.
                The log has only held whereabouts changes since {meta.activityHistoryStart ? formatDay(meta.activityHistoryStart) : 'before this window'}, so
                there is nothing to compare a year back until it has a year of history.
              </p>
              <p className="mb-2">
                Planned days, desks, parking and leave are counted by the date planned, so they reach back years. Days planned by people
                who have since been deleted still count, so old weeks do not shrink as customers leave; they are left out of the share of users planning,
                which compares current users with each other. New users only counts people still on the system.
              </p>
              <p className="mb-0">
                Each headline compares the average week over the latest {meta.comparisonWeeks} weeks with the same weeks a year earlier, which cancels
                out August and Christmas, or with the {meta.comparisonWeeks} weeks before when there is no year of history. A change within
                {` ${(meta.flatThreshold * 100).toFixed(0)}%`} either way counts as flat. The current week is left out because it is still filling in.
              </p>
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default EngagementReport;
